import React, { useEffect, useMemo, useState } from 'react'
import { collection, onSnapshot, orderBy, query, limit } from 'firebase/firestore'
import { db } from '../../firebase-config'
import NormativeDiff from './NormativeDiff'

function formatFecha(timestamp) {
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp)
  if (!date || Number.isNaN(date.getTime())) return 'Fecha no disponible'
  return date.toLocaleString('es-AR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

export default function NormativeTimeline() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState('')
  const [versionFilter, setVersionFilter] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  useEffect(() => {
    const q = query(
      collection(db, 'normative_history'),
      orderBy('timestamp', 'desc'),
      limit(40)
    )

    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      setItems(rows)
      setLoading(false)
      setSelectedId((prev) => prev || (rows[0] ? rows[0].id : ''))
    }, () => setLoading(false))

    return () => unsub()
  }, [])

  const filteredItems = useMemo(() => {
    const vf = versionFilter.trim().toLowerCase()
    const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null
    const to = toDate ? new Date(`${toDate}T23:59:59`) : null

    return items.filter((item) => {
      const version = String(item.version_id || item.id || '').toLowerCase()
      const fecha = item.timestamp?.toDate ? item.timestamp.toDate() : new Date(item.timestamp)

      if (vf && !version.includes(vf)) return false
      if (from && (!fecha || Number.isNaN(fecha.getTime()) || fecha < from)) return false
      if (to && (!fecha || Number.isNaN(fecha.getTime()) || fecha > to)) return false
      return true
    })
  }, [items, versionFilter, fromDate, toDate])

  useEffect(() => {
    if (!filteredItems.length) {
      setSelectedId('')
      return
    }
    const exists = filteredItems.some((x) => x.id === selectedId)
    if (!exists) setSelectedId(filteredItems[0].id)
  }, [filteredItems, selectedId])

  const selected = useMemo(
    () => filteredItems.find((x) => x.id === selectedId) || null,
    [filteredItems, selectedId]
  )

  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow-sm">
      <h3 className="text-lg font-bold text-cyan-100">Historial Normativo</h3>
      <p className="mt-1 text-sm text-slate-300">Linea de tiempo de cambios aprobados en el motor legal.</p>

      <div className="mt-3 grid gap-2 md:grid-cols-3">
        <input
          type="text"
          value={versionFilter}
          onChange={(e) => setVersionFilter(e.target.value)}
          placeholder="Buscar por version_id"
          className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
        />
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
        />
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
        />
      </div>

      {loading && <p className="mt-3 text-sm text-slate-400">Cargando historial...</p>}
      {!loading && items.length === 0 && <p className="mt-3 text-sm text-slate-400">Aun no hay snapshots normativos.</p>}
      {!loading && items.length > 0 && filteredItems.length === 0 && (
        <p className="mt-3 text-sm text-slate-400">Sin resultados para los filtros actuales.</p>
      )}

      {!loading && filteredItems.length > 0 && (
        <div className="mt-4 grid gap-4 lg:grid-cols-[340px_1fr]">
          <div className="max-h-[520px] space-y-2 overflow-auto pr-1">
            {filteredItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedId(item.id)}
                className={`w-full rounded-lg border px-3 py-2 text-left transition ${selectedId === item.id ? 'border-cyan-500 bg-cyan-950/40' : 'border-slate-700 bg-slate-950 hover:border-slate-500'}`}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{formatFecha(item.timestamp)}</p>
                <p className="mt-1 text-sm font-semibold text-slate-100">{item.cambio_motivo || 'Actualizacion normativa'}</p>
                <p className="mt-1 text-xs text-slate-300">Aprobado por: {item.admin_id || 'desconocido'}</p>
                <p className="text-[11px] text-slate-400">Motor: {item.hash_motor || 'N/A'}</p>
              </button>
            ))}
          </div>

          <div className="space-y-3">
            {selected && (
              <>
                <div className="rounded-xl border border-slate-700 bg-slate-950 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Version</p>
                  <p className="text-sm font-semibold text-cyan-100">{selected.version_id || selected.id}</p>
                  <p className="mt-1 text-xs text-slate-300">Fuente: {selected.fuente || 'no informada'}</p>
                </div>
                <NormativeDiff
                  anterior={selected.snapshot_anterior || {}}
                  nuevo={selected.snapshot_nuevo || {}}
                />
              </>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
